import { TextGenerateEffect, type WordItem } from '@epihub/web/src/tailwind/components/TextGenerateEffect';
import React from 'react';
import { HoverBorderGradient } from '@epihub/web/src/tailwind/components/Buttons/HoverBorderGradient';
import { IconReload } from '@tabler/icons-react';
import { Structure } from '@epihub/web/src/components/Structure';
import { styled, useTheme } from '@krishanmadan/frontend/src/theme/theme';
import { useGlobalStore } from '@krishanmadan/frontend/src/store/useGlobalStore';

const BaseText = ({ children }) => <span>{children}</span>;
const Heading = ({ children }) => {
  const theme = useTheme();
  return <h1 className='text-5xl font-bold' style={{ marginTop: 24, color: theme.v0.col.danger.hex }}>{children}</h1>;
};

const bookingFailedStories: Record<string, Record<'it' | 'en', WordItem[]>> = {
  wizard: {
    en: [
      { text: 'The wizard consulted the ancient calendar.', Wrapper: BaseText },
      { ms: 800 },
      { text: 'The stars refused to align:', Wrapper: BaseText },
      { ms: 600 },
      { text: 'Booking Failed', Wrapper: Heading },
    ],
    it: [
      { text: "Il mago consultò l'antico calendario.", Wrapper: BaseText },
      { ms: 800 },
      { text: 'Le stelle si sono rifiutate di allinearsi:', Wrapper: BaseText },
      { ms: 600 },
      { text: 'Prenotazione Fallita', Wrapper: Heading },
    ],
  },
  babel: {
    en: [
      {
        text: 'The Library of Babel contains every possible reservation.',
        Wrapper: BaseText,
      },
      { ms: 1000 },
      { text: 'Yours was in the burned wing:', Wrapper: BaseText },
      { ms: 600 },
      { text: 'Booking Failed', Wrapper: Heading },
    ],
    it: [
      {
        text: 'La Biblioteca di Babele contiene ogni possibile prenotazione.',
        Wrapper: BaseText,
      },
      { ms: 1000 },
      { text: "La tua era nell'ala bruciata:", Wrapper: BaseText },
      { ms: 600 },
      { text: 'Prenotazione Fallita', Wrapper: Heading },
    ],
  },
  philosopher: {
    en: [
      { text: 'A philosopher asked: "Can a booking fail if no one attempts it?"', Wrapper: BaseText },
      { ms: 1000 },
      { text: 'You attempted it:', Wrapper: BaseText },
      { ms: 600 },
      { text: 'Booking Failed', Wrapper: Heading },
    ],
    it: [
      { text: 'Un filosofo chiese: "Una prenotazione può fallire se nessuno ci prova?"', Wrapper: BaseText },
      { ms: 1000 },
      { text: 'Tu ci hai provato:', Wrapper: BaseText },
      { ms: 600 },
      { text: 'Prenotazione Fallita', Wrapper: Heading },
    ],
  },
  nasa: {
    en: [
      { text: 'Mission Control ran the final checks.', Wrapper: BaseText },
      { ms: 800 },
      { text: 'Houston, we have a problem:', Wrapper: BaseText },
      { ms: 600 },
      { text: 'Booking Failed', Wrapper: Heading },
    ],
    it: [
      { text: 'Il Controllo Missione ha eseguito i controlli finali.', Wrapper: BaseText },
      { ms: 800 },
      { text: 'Houston, abbiamo un problema:', Wrapper: BaseText },
      { ms: 600 },
      { text: 'Prenotazione Fallita', Wrapper: Heading },
    ],
  },
  pigeon: {
    en: [
      { text: 'A carrier pigeon was dispatched with your request.', Wrapper: BaseText },
      { ms: 1000 },
      { text: 'It never returned:', Wrapper: BaseText },
      { ms: 600 },
      { text: 'Booking Failed', Wrapper: Heading },
    ],
    it: [
      { text: 'Un piccione viaggiatore è partito con la tua richiesta.', Wrapper: BaseText },
      { ms: 1000 },
      { text: 'Non è mai tornato:', Wrapper: BaseText },
      { ms: 600 },
      { text: 'Prenotazione Fallita', Wrapper: Heading },
    ],
  },
  oracle: {
    en: [
      { text: 'The Oracle of Delphi gazed into the mists of time.', Wrapper: BaseText },
      { ms: 1000 },
      { text: 'She saw only fog:', Wrapper: BaseText },
      { ms: 600 },
      { text: 'Booking Failed', Wrapper: Heading },
    ],
    it: [
      { text: "L'Oracolo di Delfi scrutò nelle nebbie del tempo.", Wrapper: BaseText },
      { ms: 1000 },
      { text: 'Vide solo nebbia:', Wrapper: BaseText },
      { ms: 600 },
      { text: 'Prenotazione Fallita', Wrapper: Heading },
    ],
  },
  medieval: {
    en: [
      { text: 'The royal scribe dipped his quill in ink.', Wrapper: BaseText },
      { ms: 800 },
      { text: 'The inkwell was empty:', Wrapper: BaseText },
      { ms: 600 },
      { text: 'Booking Failed', Wrapper: Heading },
    ],
    it: [
      { text: "Lo scriba reale intinse la penna nell'inchiostro.", Wrapper: BaseText },
      { ms: 800 },
      { text: 'Il calamaio era vuoto:', Wrapper: BaseText },
      { ms: 600 },
      { text: 'Prenotazione Fallita', Wrapper: Heading },
    ],
  },
  ai: {
    en: [
      { text: 'The AI processed 47 billion possible outcomes.', Wrapper: BaseText },
      { ms: 1000 },
      { text: 'None of them worked:', Wrapper: BaseText },
      { ms: 600 },
      { text: 'Booking Failed', Wrapper: Heading },
    ],
    it: [
      { text: "L'IA ha elaborato 47 miliardi di possibili esiti.", Wrapper: BaseText },
      { ms: 1000 },
      { text: 'Nessuno ha funzionato:', Wrapper: BaseText },
      { ms: 600 },
      { text: 'Prenotazione Fallita', Wrapper: Heading },
    ],
  },
  gandalf: {
    en: [
      { text: 'A wizard is never late, nor is he early.', Wrapper: BaseText },
      { ms: 1000 },
      { text: 'But sometimes he simply cannot pass:', Wrapper: BaseText },
      { ms: 600 },
      { text: 'Booking Failed', Wrapper: Heading },
    ],
    it: [
      { text: 'Un mago non è mai in ritardo, né in anticipo.', Wrapper: BaseText },
      { ms: 1000 },
      { text: 'Ma a volte semplicemente non può passare:', Wrapper: BaseText },
      { ms: 600 },
      { text: 'Prenotazione Fallita', Wrapper: Heading },
    ],
  },
  captain: {
    en: [
      { text: "The captain checked the ship's manifest.", Wrapper: BaseText },
      { ms: 800 },
      { text: 'The ship had already sailed:', Wrapper: BaseText },
      { ms: 600 },
      { text: 'Booking Failed', Wrapper: Heading },
    ],
    it: [
      { text: 'Il capitano controllò il manifesto della nave.', Wrapper: BaseText },
      { ms: 800 },
      { text: 'La nave era già salpata:', Wrapper: BaseText },
      { ms: 600 },
      { text: 'Prenotazione Fallita', Wrapper: Heading },
    ],
  },
  simple: {
    en: [
      { text: 'We checked. We double-checked.', Wrapper: BaseText },
      { ms: 800 },
      { text: "It didn't go through:", Wrapper: BaseText },
      { ms: 600 },
      { text: 'Booking Failed', Wrapper: Heading },
    ],
    it: [
      { text: 'Abbiamo controllato. Ricontrollato.', Wrapper: BaseText },
      { ms: 800 },
      { text: 'Non è andata a buon fine:', Wrapper: BaseText },
      { ms: 600 },
      { text: 'Prenotazione Fallita', Wrapper: Heading },
    ],
  },
  schrodinger: {
    en: [
      { text: 'Your booking was both confirmed and failed.', Wrapper: BaseText },
      { ms: 1000 },
      { text: 'We observed it:', Wrapper: BaseText },
      { ms: 600 },
      { text: 'Booking Failed', Wrapper: Heading },
    ],
    it: [
      { text: 'La tua prenotazione era sia confermata che fallita.', Wrapper: BaseText },
      { ms: 1000 },
      { text: "L'abbiamo osservata:", Wrapper: BaseText },
      { ms: 600 },
      { text: 'Prenotazione Fallita', Wrapper: Heading },
    ],
  },
};

const storyKeys = Object.keys(bookingFailedStories);
const randomStory = () => bookingFailedStories[storyKeys[Math.floor(Math.random() * storyKeys.length)]];

type Props = {
  onRetry: () => void;
};

export const CalendarErrorView = ({ onRetry }: Props) => {
  const language = useGlobalStore((store) => store.settings.language);
  const [story] = React.useState(randomStory);

  const theme = useTheme();
  return (
    <Root>
      <Center>
        <Structure hcenter>
          <TextGenerateEffect
            words={story[language]}
          />
          <ButtonWrapper>
            <HoverBorderGradient
              containerClassName='rounded-full'
              as='button'
              className='dark:bg-black bg-white text-black dark:text-white flex items-center space-x-2 overridepadding'
              style={{
                cursor: 'pointer',
              }}
              onClick={onRetry}
            >
              <IconReload color={theme.v0.col.danger.hex} />
              <span>Retry</span>
            </HoverBorderGradient>
          </ButtonWrapper>
        </Structure>
      </Center>
    </Root>
  );
};

const Root = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
`;

const Center = styled.div`
  max-width: 500px;
  margin-top: 24px;
  @media (max-width: 769px) {
    max-width: 70vw;
  }
  
  display: flex;
  align-items: center;
  justify-content: center;
  text-align: center;
`;

const ButtonWrapper = styled.div`
  margin-top: 48px;
  :active {
    transform: scale(0.90);
  }
  .overridepadding {
    padding-block: 9px !important;
    padding-inline: 26px !important;
  }
`;
